///
/// EarthBrowser
///
/// Created by Matt Giger on 9/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import sgp4init from "@/earthbrowser/libs/sgp4/propagation/sgp4init"
import { wgs } from "@/earthbrowser/utils/geographic"
import type { SatelliteEntry } from "@shared-types/satellite-entry"
import { time, trig, vec3 } from "@stovokor/math"
import { ecfToLookAngles, eciToEcf, eciToGeodetic, gstime, propagate, type SatRec } from "satellite.js"

/** Minimum elevation above the horizon (degrees) for a usable pass */
export const cMinPassElevationDeg = 10

const cPassSearchHours = 48
const cPassSearchStepSec = 30

export interface ObserverLocation {
	lat: number
	lon: number
}

export interface SatelliteLookAngles {
	azimuthDeg: number
	elevationDeg: number
	rangeKm: number
}

export type PassPrediction =
	| { kind: "pass", rise: Date, set: Date, maxElevationDeg: number, riseAzimuthDeg: number }
	| { kind: "geostationary", visible: boolean, azimuthDeg: number, elevationDeg: number }
	| { kind: "none", searchHours: number }

/**
 * Compute the satellite's position in earth-fixed world coordinates (km)
 * using the same spherical mapping as SatelliteNode rendering.
 */
export function orbitWorldPosition(satrec: SatRec, date: Date): vec3 | undefined {
	const pv = propagate(satrec, date)
	if(!(pv?.position instanceof Object)) {
		return undefined
	}
	const gmst = gstime(date)
	const lla = eciToGeodetic(pv.position, gmst)
	const elev = lla.height + wgs.a
	const coslat = Math.cos(lla.latitude) * elev
	return new vec3(
		Math.sin(lla.longitude) * coslat,
		Math.sin(lla.latitude) * elev,
		Math.cos(lla.longitude) * coslat
	)
}

/**
 * Sample the orbit path in earth-fixed world coordinates over a time span.
 * Both endpoints are included. Invalid propagation samples are skipped.
 */
export function sampleOrbitPath(satrec: SatRec, start: Date, durationSec: number, samples: number): vec3[] {
	const positions: vec3[] = []
	for(let i = 0; i <= samples; i++) {
		const date = new Date(start.getTime() + (i / samples) * durationSec * 1000)
		const position = orbitWorldPosition(satrec, date)
		if(position) {
			positions.push(position)
		}
	}
	return positions
}

/**
 * Azimuth/elevation/range of the satellite as seen from an observer on the ground
 */
export function lookAnglesAt(satrec: SatRec, observer: ObserverLocation, date: Date): SatelliteLookAngles | undefined {
	const pv = propagate(satrec, date)
	if(!(pv?.position instanceof Object)) {
		return undefined
	}
	const gmst = gstime(date)
	const ecf = eciToEcf(pv.position, gmst)
	const look = ecfToLookAngles({
		latitude: observer.lat * trig.rad,
		longitude: observer.lon * trig.rad,
		height: 0
	}, ecf)
	return {
		azimuthDeg: look.azimuth * trig.deg,
		elevationDeg: look.elevation * trig.deg,
		rangeKm: look.rangeSat
	}
}

/**
 * Predict the next pass of a satellite over an observer location.
 *
 * Searches up to 48 hours ahead for the next window where the satellite rises
 * above cMinPassElevationDeg. Geosynchronous satellites are reported by their
 * fixed visibility instead, since their geometry never changes.
 */
export function predictNextPass(satrec: SatRec, observer: ObserverLocation, from: Date = new Date()): PassPrediction {
	// Geosynchronous: visibility is constant, report the current geometry
	const revsPerDay = satrec.no * 1440 / trig.two_pi
	if(revsPerDay > 0.9 && revsPerDay < 1.1) {
		const look = lookAnglesAt(satrec, observer, from)
		return {
			kind: "geostationary",
			visible: (look?.elevationDeg ?? -90) > 0,
			azimuthDeg: look?.azimuthDeg ?? 0,
			elevationDeg: look?.elevationDeg ?? 0
		}
	}

	const stepMs = cPassSearchStepSec * 1000
	const endMs = from.getTime() + cPassSearchHours * 3600 * 1000

	let riseMs: number | undefined
	let riseAzimuthDeg = 0
	let maxElevationDeg = -90
	let prevMs = from.getTime()

	const first = lookAnglesAt(satrec, observer, from)
	if(!first) {
		return { kind: "none", searchHours: cPassSearchHours }
	}
	if(first.elevationDeg >= cMinPassElevationDeg) {
		// Pass already in progress
		riseMs = from.getTime()
		riseAzimuthDeg = first.azimuthDeg
		maxElevationDeg = first.elevationDeg
	}

	for(let ms = from.getTime() + stepMs; ms <= endMs; ms += stepMs) {
		const look = lookAnglesAt(satrec, observer, new Date(ms))
		if(!look) {
			prevMs = ms
			continue
		}
		const above = look.elevationDeg >= cMinPassElevationDeg

		if(above && riseMs === undefined) {
			riseMs = refineCrossing(satrec, observer, prevMs, ms)
			riseAzimuthDeg = lookAnglesAt(satrec, observer, new Date(riseMs))?.azimuthDeg ?? look.azimuthDeg
		}
		if(riseMs !== undefined) {
			maxElevationDeg = Math.max(maxElevationDeg, look.elevationDeg)
			if(!above) {
				const setMs = refineCrossing(satrec, observer, prevMs, ms)
				return { kind: "pass", rise: new Date(riseMs), set: new Date(setMs), maxElevationDeg, riseAzimuthDeg }
			}
		}
		prevMs = ms
	}

	// Pass started but the search window ended before the set time
	if(riseMs !== undefined) {
		return { kind: "pass", rise: new Date(riseMs), set: new Date(endMs), maxElevationDeg, riseAzimuthDeg }
	}
	return { kind: "none", searchHours: cPassSearchHours }
}

/**
 * Bisect between two timestamps to find where elevation crosses the pass threshold
 */
function refineCrossing(satrec: SatRec, observer: ObserverLocation, lowMs: number, highMs: number): number {
	let lo = lowMs
	let hi = highMs
	const loAbove = (lookAnglesAt(satrec, observer, new Date(lo))?.elevationDeg ?? -90) >= cMinPassElevationDeg
	for(let i = 0; i < 12; i++) {
		const mid = (lo + hi) / 2
		const midAbove = (lookAnglesAt(satrec, observer, new Date(mid))?.elevationDeg ?? -90) >= cMinPassElevationDeg
		if(midAbove === loAbove) {
			lo = mid
		} else {
			hi = mid
		}
	}
	return (lo + hi) / 2
}

const cCompassPoints = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE", "S", "SSW", "SW", "WSW", "W", "WNW", "NW", "NNW"]

/** Convert an azimuth in degrees to a 16-point compass direction */
export function compassDirection(azimuthDeg: number): string {
	const normalized = ((azimuthDeg % 360) + 360) % 360
	return cCompassPoints[Math.round(normalized / 22.5) % 16]
}

interface initoptions {
	opsmode: string
	satn: string
	epoch: number
	xbstar: number
	xecco: number
	xargpo: number
	xinclo: number
	xmo: number
	xno: number
	xnodeo: number
}

/**
 * Fix PostgreSQL timestamp string to valid ISO 8601
 * Converts: '2025-12-20 14:31:10.4664+00' -> '2025-12-20T14:31:10.4664Z'
 */
function fixIsoDateString(dateString: string): string {
	// Replace space with T
	let fixed = dateString.replace(' ', 'T')

	// Fix timezone: +00 -> Z, or ensure proper +HH:MM format
	if (fixed.endsWith('+00')) {
		fixed = fixed.replace('+00', 'Z')
	} else if (fixed.endsWith('-00')) {
		fixed = fixed.replace('-00', 'Z')
	} else if (/[+-]\d{2}$/.test(fixed)) {
		// If it's like +05 or -08, add :00
		fixed = fixed.replace(/([+-]\d{2})$/, '$1:00')
	}

	return fixed
}

/**
 * Build an SGP4 satellite record from stored orbital elements
 */
export function satrecFrom(entry: SatelliteEntry): SatRec {

	const epoch = new time(fixIsoDateString(entry.epoch))
	const epochYear = epoch.date.getUTCFullYear()
	const epochDays = epoch.julian - epoch.yearStart.julian

	const options: initoptions = {
		opsmode: 'i',
		satn: String(entry.norad_cat_id),
		epoch: epoch.julian - 2433281.5,
		xbstar: entry.bstar,
		xecco: entry.eccentricity,
		xargpo: entry.arg_of_pericenter * trig.rad,
		xinclo: entry.inclination * trig.rad,
		xmo: entry.mean_anomaly * trig.rad,
		xno: entry.mean_motion * trig.two_pi / 1440.0,  // Convert revs/day to radians/minute
		xnodeo: entry.ra_of_asc_node * trig.rad,
	}

	// what a crazy interface, we have to pass these in as well
	const rec = {
		satnum: String(entry.norad_cat_id),
		epochyr: epochYear,
		epochdays: epochDays,
		jdsatepoch: epoch.julian,
		ndot: entry.mean_motion_dot,
		nddot: entry.mean_motion_ddot,
	} as SatRec

	sgp4init(rec, options)

	return rec
}
